/**
 * Grafy kreslené jako SVG (bez knihoven): spad varroázy během roku a kalendář sezóny.
 * Barvy jdou přes CSS proměnné, takže fungují ve světlém i tmavém režimu i při tisku.
 */
import { useState } from 'react';
import type { ISODate } from '../db/model';
import type { CalendarRow, VarroaSeries } from '../db/reports';
import { dayIndex } from '../db/reports';
import { colonyName, fmtDate, plural } from './labels';

const MONTH_SHORT = ['led', 'úno', 'bře', 'dub', 'kvě', 'čvn', 'čvc', 'srp', 'zář', 'říj', 'lis', 'pro'];

function monthStarts(from: ISODate, to: ISODate): { date: ISODate; m: number }[] {
  const out: { date: ISODate; m: number }[] = [];
  let y = Number(from.slice(0, 4));
  let m = Number(from.slice(5, 7));
  for (;;) {
    const d = `${y}-${String(m).padStart(2, '0')}-01`;
    if (d > to) break;
    if (d >= from) out.push({ date: d, m });
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return out;
}

/** Hezké maximum osy y (10, 20, 50, 100, 200, …). */
export function niceMax(v: number): number {
  // NaN/nekonečno by smyčku níže nikdy neukončilo (obrazovka by zamrzla)
  if (!Number.isFinite(v) || v <= 10) return 10;
  const steps = [10, 20, 25, 50];
  let base = 1;
  for (;;) {
    for (const s of steps) if (s * base >= v) return s * base;
    base *= 10;
  }
}

export function VarroaChart({
  series,
  from,
  to,
  yMax,
  compact,
  title,
}: {
  series: VarroaSeries;
  from: ISODate;
  to: ISODate;
  /** Společné maximum pro porovnání více grafů. */
  yMax?: number;
  compact?: boolean;
  title?: string;
}) {
  const W = 340;
  const H = compact ? 110 : 180;
  const L = compact ? 26 : 34;
  const R = 6;
  const T = compact ? 10 : 18;
  const B = compact ? 16 : 22;
  const days = Math.max(1, dayIndex(to, from));
  const x = (d: ISODate) => L + (dayIndex(d, from) / days) * (W - L - R);
  const max = yMax ?? niceMax(Math.max(10, ...series.falls.map((f) => f.mites)));
  const y = (v: number) => T + (1 - v / max) * (H - T - B);
  const bw = compact ? 5 : 8;
  const months = monthStarts(from, to);
  const label = title ?? 'Spad roztočů během roku';
  const [picked, setPicked] = useState<string | null>(null);
  const pick = (text: string) => !compact && setPicked((p) => (p === text ? null : text));
  const fallText = (f: VarroaSeries['falls'][0]) =>
    `${fmtDate(f.date)}: ${plural(f.mites, 'roztoč', 'roztoči', 'roztočů')}${f.days ? ` za ${f.days} d` : ''}${f.afterTreatment ? ' (spad po ošetření)' : ''}`;
  const svg = (
    <svg className={'chart varroa-chart' + (compact ? ' compact' : '')} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
      <title>{label}</title>
      {/* mřížka a osa y */}
      {[0, 0.5, 1].map((f) => (
        <g key={f}>
          <line className="grid" x1={L} x2={W - R} y1={y(max * f)} y2={y(max * f)} />
          <text className="axis" x={L - 4} y={y(max * f) + 3.5} textAnchor="end">
            {Math.round(max * f)}
          </text>
        </g>
      ))}
      {months.map(({ date, m }, i) => (
        <g key={date}>
          <line className="grid month" x1={x(date)} x2={x(date)} y1={T} y2={H - B} />
          {(!compact || i % 2 === 0) && (
            <text className="axis" x={x(date) + 2} y={H - B + (compact ? 11 : 14)}>
              {MONTH_SHORT[m - 1]}
            </text>
          )}
        </g>
      ))}
      {/* léčení */}
      {series.treatments.map((t, i) => (
        <g key={'t' + i} className="treat" onClick={() => pick(`${fmtDate(t.date)}: léčení ${t.product}`)}>
          <line x1={x(t.date)} x2={x(t.date)} y1={T - (compact ? 4 : 8)} y2={H - B} />
          <path d={`M${x(t.date) - 4},${T - (compact ? 8 : 14)} h8 l-4,6 z`} />
          {!compact && <rect className="hit" x={x(t.date) - 7} y={T - 16} width={14} height={14} />}
          <title>
            {fmtDate(t.date)}: léčení {t.product}
          </title>
        </g>
      ))}
      {/* spady */}
      {series.falls.map((f, i) => (
        <g key={'f' + i} className={'fall' + (f.afterTreatment ? ' after' : '')} onClick={() => pick(fallText(f))}>
          {!compact && <rect className="hit" x={x(f.date) - 9} y={T} width={18} height={H - B - T} />}
          <rect x={x(f.date) - bw / 2} y={y(Math.min(f.mites, max))} width={bw} height={Math.max(1, H - B - y(Math.min(f.mites, max)))} rx={1.5} />
          {!compact && (
            <text className="val" x={x(f.date)} y={y(Math.min(f.mites, max)) - 3} textAnchor="middle">
              {f.mites}
            </text>
          )}
          <title>{fallText(f)}</title>
        </g>
      ))}
      <line className="baseline" x1={L} x2={W - R} y1={H - B} y2={H - B} />
    </svg>
  );
  if (compact) return svg;
  return (
    <>
      {svg}
      <p className="chart-pick small" aria-live="polite">
        {picked ?? 'Ťukněte na sloupec nebo značku léčení – ukáže se datum a hodnota.'}
      </p>
    </>
  );
}

export function VarroaLegend() {
  return (
    <div className="chart-legend small muted">
      <span>
        <i className="lg fall" /> spad na podložce
      </span>
      <span>
        <i className="lg after" /> spad po ošetření
      </span>
      <span>
        <i className="lg treat" /> léčení
      </span>
    </div>
  );
}

/** Kalendář sezóny: řádek na včelstvo, pruh = nasazené medníky, značky zásahů. */
export function SeasonCalendar({ rows, from, to }: { rows: CalendarRow[]; from: ISODate; to: ISODate }) {
  const W = 340;
  const L = 64;
  const R = 6;
  const RH = 26;
  const T = 16;
  const H = T + rows.length * RH + 4;
  const days = Math.max(1, dayIndex(to, from));
  const x = (d: ISODate) => L + (Math.min(days, Math.max(0, dayIndex(d, from))) / days) * (W - L - R);
  const months = monthStarts(from, to);
  return (
    <svg className="chart season-cal" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Kalendář sezóny">
      <title>Kalendář sezóny</title>
      {months.map(({ date, m }) => (
        <g key={date}>
          <line className="grid month" x1={x(date)} x2={x(date)} y1={T - 4} y2={H} />
          <text className="axis" x={x(date) + 2} y={10}>
            {MONTH_SHORT[m - 1]}
          </text>
        </g>
      ))}
      {rows.map((r, i) => {
        const cy = T + i * RH + RH / 2;
        return (
          <g key={r.colony.id}>
            {i % 2 === 1 && <rect className="stripe" x={0} y={cy - RH / 2} width={W} height={RH} />}
            <text className="rowlabel" x={4} y={cy + 4}>
              {colonyName(r.colony.label).length > 10 ? colonyName(r.colony.label).slice(0, 9) + '…' : colonyName(r.colony.label)}
            </text>
            {r.supers.map((s, j) => {
              const h = Math.min(16, 6 + s.count * 4);
              return (
                <g key={'s' + j} className="super">
                  <rect x={x(s.from)} y={cy - h / 2} width={Math.max(2, x(s.to) - x(s.from))} height={h} rx={3} />
                  {x(s.to) - x(s.from) > 14 && (
                    <text x={x(s.from) + 3} y={cy + 3.5}>
                      {s.count}
                    </text>
                  )}
                  <title>
                    {s.count}× medník {fmtDate(s.from)} – {fmtDate(s.to)}
                  </title>
                </g>
              );
            })}
            {r.inspections.map((d, j) => (
              <line key={'i' + j} className="insp" x1={x(d)} x2={x(d)} y1={cy + 8} y2={cy + 12}>
                <title>{fmtDate(d)}: prohlídka</title>
              </line>
            ))}
            {r.feedings.map((d, j) => (
              <circle key={'f' + j} className="feed" cx={x(d)} cy={cy} r={3.2}>
                <title>{fmtDate(d)}: krmení</title>
              </circle>
            ))}
            {r.harvests.map((d, j) => (
              <path key={'h' + j} className="harv" d={`M${x(d)},${cy - 5} l4,5 l-4,5 l-4,-5 z`}>
                <title>{fmtDate(d)}: medobraní</title>
              </path>
            ))}
            {r.treatments.map((d, j) => (
              <path key={'t' + j} className="treatm" d={`M${x(d) - 4},${cy - 9} h8 l-4,6 z`}>
                <title>{fmtDate(d)}: léčení</title>
              </path>
            ))}
          </g>
        );
      })}
    </svg>
  );
}

export function CalendarLegend() {
  return (
    <div className="chart-legend small muted">
      <span>
        <i className="lg super" /> medníky (číslo = počet)
      </span>
      <span>
        <i className="lg harv" /> medobraní
      </span>
      <span>
        <i className="lg treat" /> léčení
      </span>
      <span>
        <i className="lg feed" /> krmení
      </span>
      <span>
        <i className="lg insp" /> prohlídka
      </span>
    </div>
  );
}
